import { GoArrowRight, GoChevronLeft } from "react-icons/go";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { nextStep, previousStep } from "@/store/features/wizardSlice";
import { twMerge } from "tailwind-merge";
import { useRef } from "react";
import dynamic from "next/dynamic";
import { WizardSteps } from "@/store";
import Orderform from "./Orderform";

const CardCapture = dynamic(() => import("@/components/CardCapture"));
const ComplianceVideo = dynamic(() => import("@/components/ComplianceVideo"));
const DocumentSigning = dynamic(() => import("@/components/DocumentSigning"));
const OncehubBooking = dynamic(() => import("@/components/OncehubBooking"));
const HubspotBooking = dynamic(() => import("@/components/HubspotBooking"));
const ThankYou = dynamic(() => import("@/components/Thankyou"));
const Welcome = dynamic(() => import("@/components/Welcome"));

// see Basement for definitions, or reverse :)
// path app/(private)/editor/onboarding/workflowtemplate/[wfTemplateId]/Steps.js
const ST_WELCOME = "salesonboard-welcome";
const ST_ORDERFORM = "orderformlink";
const ST_WATCHVIDEO = "salesonboard-compliance-video";
const ST_PANDADOC = "salesonboard-panda";
const ST_CARDCAPTURE = "card-capture";
const ST_BOOKING = "thank-you-orientation-schedule";
const ST_HUBSPOT_BOOKING = "hubspot-booking";
const ST_THANKYOU = "thank-you";

const getWizardStep = ({
  stepNumber,
  continueHook,
  steps,
}: {
  stepNumber: number;
  steps: WizardSteps;
  continueHook: any;
}) => {
  const currentStep = steps[stepNumber] || ({} as WizardSteps);

  switch (currentStep?.id) {
    case ST_HUBSPOT_BOOKING: {
      return <HubspotBooking step={currentStep} />;
    }
    case ST_WELCOME: {
      return <Welcome step={currentStep} />;
    }
    case ST_ORDERFORM: {
      return <Orderform step={currentStep} />;
    }
    case ST_WATCHVIDEO: {
      return <ComplianceVideo step={currentStep} />;
    }
    case ST_PANDADOC: {
      return <DocumentSigning step={currentStep} />;
    }
    case ST_CARDCAPTURE: {
      return <CardCapture continueHook={continueHook} step={currentStep} />;
    }
    case ST_BOOKING: {
      return <OncehubBooking step={currentStep} />;
    }
    case ST_THANKYOU: {
      return <ThankYou step={currentStep} />;
    }
    default: {
      console.log("FALLING BACK to thankyou");
      return <ThankYou step={currentStep} />;
    }
  }
};

export default function Wizard({
  // eslint-disable-next-line no-unused-vars
  user,
}: {
  user: { firstName: string; lastName: string; email: string };
}) {
  const dispatch = useAppDispatch();

  const wizardState = useAppSelector((state) => state.wizard);
  const {
    steps,
    stepNumber,
    totalSteps: numberSteps,
    canGoBack,
    loading,
  } = wizardState;

  // console.log("Wizard state", JSON.stringify(wizardState, null, 2));

  const continueHook: any = useRef();

  const onNext = async () => {
    try {
      console.log("onNext", stepNumber);
      // tap into addcard here on step 4
      if (continueHook.current) {
        console.log("Run child hook before Continue");
        await continueHook.current();
      }
      // }

      console.log("Clear child hook");
      continueHook.current = null;

      if (stepNumber === numberSteps) {
        // no more steps here
        return;
      }
      dispatch(nextStep());
    } catch (err) {
      console.log("error in onNext", err);
    }
  };
  const onPrevious = () => {
    if (stepNumber === 1 || !canGoBack) {
      return;
    }
    dispatch(previousStep());
  };

  const isButtonDisabled = () => {
    // @ts-ignore
    return (
      // currentStep > 1 && // step 1 should not be restrained
      // @ts-ignore
      !steps[stepNumber]?.done
    );
  };

  return (
    <div>
      {getWizardStep({
        stepNumber,
        steps, // build flow from defined steps in workflow instead of hardcode
        continueHook,
      })}
      <div className="flex flex-col gap-3 items-center">
        {stepNumber !== numberSteps && (
          <button
            type="button"
            className={twMerge(
              "group/next mx-auto rounded-sm w-3/4 md:w-96 py-2 text-md font-semibold text-white flex items-center justify-center gap-1 shadow-lg",
              isButtonDisabled() || loading
                ? "bg-gray-400 text-gray-300  cursor-not-allowed"
                : "bg-primary hover:bg-primary/90 animate-pulse",
            )}
            onClick={onNext}
            disabled={isButtonDisabled()}
          >
            Continue
            <GoArrowRight
              className={twMerge(
                !isButtonDisabled() &&
                  "group-hover/next:translate-x-1 transition-all",
              )}
            />
          </button>
        )}
        {stepNumber === 1 || stepNumber === numberSteps || !canGoBack ? null : (
          <button
            type="button"
            className="flex gap-1 items-center text-sm text-gray-400/90 hover:text-gray-600"
            onClick={onPrevious}
          >
            <GoChevronLeft />
            Back To Previous Step
          </button>
        )}
      </div>
    </div>
  );
}
